import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { LogOut, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";
import { NewTermsStep } from "./steps/NewTermsStep";
import { NewCashbackStep } from "./steps/NewCashbackStep";
import { SmsTemplateStep } from "./steps/SmsTemplateStep";
import { NewProfileStep } from "./steps/NewProfileStep";
import { NuvemshopIntegrationStep } from "./steps/NuvemshopIntegrationStep";
import { NewWelcomeStep } from "./steps/NewWelcomeStep";
interface NewOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const steps = [{
  id: 'terms',
  title: 'Termos de Uso',
  icon: '1'
}, {
  id: 'cashback',
  title: 'Configurar Cashback',
  icon: '2'
}, {
  id: 'sms-template',
  title: 'Template SMS',
  icon: '3'
}, {
  id: 'profile',
  title: 'Completar Perfil',
  icon: '4'
}, {
  id: 'nuvemshop-integration',
  title: 'Conectar Loja',
  icon: '5'
}, {
  id: 'welcome',
  title: 'Finalizar',
  icon: '6'
}];
export const NewOnboardingModal = ({
  isOpen,
  onClose
}: NewOnboardingModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { logout } = useAuth();
  const { progress, updateProgress, completeOnboarding } = useOnboardingProgress();

  // Handler seguro para logout
  const handleSafeLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Se houver erro no logout, ainda assim redirecionar
      console.warn('Logout error handled gracefully:', error);
      window.location.href = '/login';
    }
  };

  // Determinar etapa atual baseado no progresso (corrigida)
  useEffect(() => {
    if (progress) {
      console.log('🔄 Determining current step based on progress:', progress);
      if (!progress.terms_accepted) {
        console.log('📝 Setting step 0: Terms not accepted');
        setCurrentStep(0);
      } else if (!progress.cashback_configured) {
        console.log('💰 Setting step 1: Cashback not configured');
        setCurrentStep(1);
      } else if (!progress.sms_template_configured) {
        console.log('📱 Setting step 2: SMS template not configured');
        setCurrentStep(2);
      } else if (!progress.nuvemshop_integrated) {
        console.log('🏪 Setting step 3: Profile step (before Nuvemshop)');
        setCurrentStep(3);
      } else if (progress.nuvemshop_integrated && !progress.completed_at) {
        console.log('🎉 Setting step 5: Nuvemshop integrated, going to welcome');
        setCurrentStep(5);
      } else {
        console.log('✅ All completed');
        setCurrentStep(5);
      }
    }
  }, [progress]);
  const handleNext = () => {
    console.log('🚀 handleNext called, currentStep:', currentStep, 'steps.length:', steps.length);
    if (currentStep < steps.length - 1) {
      console.log('➡️ Moving to next step:', currentStep + 1);
      setCurrentStep(currentStep + 1);
    } else {
      console.log('🛑 Already at last step');
    }
  };
  const handlePrevious = () => {
    console.log('⬅️ handlePrevious called, currentStep:', currentStep);
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleStepComplete = async (stepId: string, data?: any) => {
    console.log('✅ handleStepComplete called:', stepId, data);
    const updates: any = {};
    switch (stepId) {
      case 'terms':
        updates.terms_accepted = true;
        break;
      case 'cashback':
        updates.cashback_configured = true;
        break;
      case 'sms-template':
        updates.sms_template_configured = true;
        break;
      case 'profile':
        // Para o step de perfil, apenas avançar para a próxima etapa
        console.log('👤 Profile step completed, moving to next step');
        handleNext();
        return;
      case 'nuvemshop-integration':
        console.log('🏪 Nuvemshop integration completed');
        updates.nuvemshop_integrated = true;
        break;
      case 'welcome':
        console.log('🎉 Welcome step completed, finishing onboarding');
        await completeOnboarding();
        window.location.href = '/integrations';
        return;
    }
    if (Object.keys(updates).length > 0) {
      console.log('💾 Updating progress with:', updates);
      await updateProgress(updates);
      console.log('✅ Progress updated, calling handleNext');
    }
    handleNext();
  };
  const getStepProgress = () => {
    return (currentStep + 1) / steps.length * 100;
  };
  const renderCurrentStep = () => {
    console.log('🎭 Rendering step:', currentStep, 'stepId:', steps[currentStep]?.id);
    switch (currentStep) {
      case 0:
        return <NewTermsStep onComplete={() => handleStepComplete('terms')} />;
      case 1:
        return <NewCashbackStep onComplete={() => handleStepComplete('cashback')} onPrevious={handlePrevious} />;
      case 2:
        return <SmsTemplateStep onComplete={() => handleStepComplete('sms-template')} onPrevious={handlePrevious} />;
      case 3:
        return <NewProfileStep onComplete={() => handleStepComplete('profile')} onPrevious={handlePrevious} />;
      case 4:
        console.log('🎭 Rendering NuvemshopIntegrationStep');
        return <NuvemshopIntegrationStep onNext={() => {
          console.log('🎯 NuvemshopIntegrationStep onNext called');
          handleStepComplete('nuvemshop-integration');
        }} onPrev={handlePrevious} onIntegrationSuccess={() => {
          console.log('🎯 Integration success callback triggered');
          handleStepComplete('nuvemshop-integration');
        }} />;
      case 5:
        return <NewWelcomeStep onComplete={() => handleStepComplete('welcome')} onPrevious={handlePrevious} />;
      default:
        console.log('❌ Unknown step:', currentStep);
        return null;
    }
  };
  return <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-5xl w-full max-h-[90vh] p-0 bg-card border gap-1">
        <div className="sr-only">
          <DialogTitle>Configuração do Sistema</DialogTitle>
          <DialogDescription>
            Configure sua conta e integre sua loja em etapas simples
          </DialogDescription>
        </div>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <h1 className="text-lg font-semibold text-card-foreground">Configuração</h1>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
              {Math.round(getStepProgress())}%
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={handleSafeLogout} className="h-8">
            <LogOut className="w-3 h-3 mr-1" />
            <span className="text-xs hidden sm:inline">Sair</span>
          </Button>
        </div>

        {/* Progress */}
        <div className="px-4 py-3 border-b border-border bg-muted/30">
          <Progress value={getStepProgress()} className="h-2 mb-3" />
          
          {/* Step Indicators */}
          <div className="flex items-center justify-between space-x-2 sm:space-x-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center space-x-1 sm:space-x-2 min-w-0">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors flex-shrink-0 ${index < currentStep ? 'bg-primary text-primary-foreground' : index === currentStep ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'}`}>
                  {index < currentStep ? <Check className="w-3 h-3" /> : step.icon}
                </div>
                <span className={`text-xs sm:text-sm font-medium truncate ${index <= currentStep ? 'text-card-foreground' : 'text-muted-foreground'}`}>
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto max-h-[60vh] py-2 pb-3 px-4">
          {renderCurrentStep()}
        </div>
      </DialogContent>
    </Dialog>;
};